import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, LogIn } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiAcceptInvite } from "../lib/api";
import { Logo } from "../components/Logo";

export default function AcceptInvite() {
  const { token } = useParams();
  const { user, ready } = useAuth();
  const [state, setState] = useState({ status: "working", org: null });
  const attempted = useRef(false);

  useEffect(() => {
    if (!ready || !user || attempted.current) return;
    attempted.current = true;
    apiAcceptInvite(token)
      .then((r) => setState({ status: "ok", org: r.org_name }))
      .catch(() => setState({ status: "error", org: null }));
  }, [ready, user, token]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-5" data-testid="accept-invite-page">
      <div className="w-full max-w-md rounded-3xl border border-border bg-white p-10 text-center shadow-sm">
        <div className="mb-8 flex justify-center"><Logo /></div>

        {!ready || (user && state.status === "working") ? (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand" />
            <p className="mt-5 text-sm text-muted-foreground">Accepting your invitation…</p>
          </>
        ) : user === false ? (
          <>
            <LogIn className="mx-auto h-10 w-10 text-brand" />
            <h1 className="mt-5 font-display text-xl font-bold text-navy">Sign in to accept</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              You were invited to join a team workspace. Sign in — or create a free account — and then
              open this invitation link again to join.
            </p>
            <Link to="/login" data-testid="invite-login-link" className="mt-6 inline-flex items-center justify-center rounded-full bg-aero px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
              Go to sign in
            </Link>
          </>
        ) : state.status === "ok" ? (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <h1 className="mt-5 font-display text-xl font-bold text-navy">You're in!</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              You've joined <span className="font-semibold text-navy">{state.org}</span>. The shared document
              library and AI modules are ready for you.
            </p>
            <Link to="/dashboard" data-testid="invite-dashboard-link" className="mt-6 inline-flex items-center justify-center rounded-full bg-aero px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
              Open dashboard
            </Link>
          </>
        ) : (
          <>
            <XCircle className="mx-auto h-10 w-10 text-destructive" />
            <h1 className="mt-5 font-display text-xl font-bold text-navy">Invitation not valid</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              This invitation link is invalid or has already been used. Ask your workspace admin to send a new one.
            </p>
            <Link to="/dashboard" className="mt-6 inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-semibold text-navy hover:bg-secondary">
              Go to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
